import React from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from "@material-ui/core";
import { Field } from "formik";

/**
 * Componente compartido para pestaña de Recurrencia
 * Fecha: 2026-01-08
 * Descripción: Componente reutilizable para configurar recurrencia (Semanal/Mensual/Anual)
 * ⚠️ IMPORTANTE: NO incluye tipo "Diaria" con horarios múltiples (ese es específico de GroupCampaigns)
 * 
 * @param {Object} values - Valores del formulario (Formik)
 * @param {Function} setFieldValue - Función para actualizar valores (Formik)
 * @param {Object} touched - Objeto touched de Formik
 * @param {Object} errors - Objeto errors de Formik
 * @param {boolean} disabled - Si los campos están deshabilitados
 * @param {Object} classes - Clases CSS del componente padre (opcional)
 */
const RecurrenceTab = ({
  values,
  setFieldValue,
  touched = {},
  errors = {},
  disabled = false,
  classes = {}
}) => {
  // Función helper para parsear recurrenceDaysOfWeek
  const parseDaysOfWeek = (value) => {
    try {
      if (!value || value === "" || value === null) {
        return [];
      }
      // Intentar parsear como JSON
      let parsed;
      if (typeof value === 'string') {
        parsed = JSON.parse(value);
      } else if (Array.isArray(value)) {
        parsed = value;
      } else {
        return [];
      }
      // Asegurar que sea un array de números
      if (Array.isArray(parsed)) {
        return parsed.map(d => typeof d === 'number' ? d : parseInt(d, 10)).filter(d => !isNaN(d));
      }
      return [];
    } catch (e) {
      // Si falla el parse, intentar como string de números separados por comas
      try {
        const str = String(value).replace(/[[\]]/g, '');
        const numbers = str.split(',').map(n => parseInt(n.trim(), 10)).filter(n => !isNaN(n));
        return numbers;
      } catch (e2) {
        return [];
      }
    }
  };

  const selectedDays = parseDaysOfWeek(values.recurrenceDaysOfWeek);
  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  return (
    <Grid xs={12} item>
      <Typography variant="h6" style={{ marginBottom: '16px' }}>
        <span role="img" aria-label="Recurrencia">🔄</span> Configuración Recurrente
      </Typography>

      <Grid spacing={2} container style={{ marginTop: '8px' }}>
        {/* Tipo de Recurrencia */}
        <Grid xs={12} md={6} item>
          <FormControl 
            variant="outlined" 
            fullWidth 
            margin="dense"
            className={`${classes.formControl || ''} ${classes.selectField || ''}`}
            error={touched.recurrenceType && Boolean(errors.recurrenceType)}
          >
            <InputLabel id="recurrenceType-selection-label">
              Tipo de Recurrencia
            </InputLabel>
            <Field
              as={Select}
              name="recurrenceType"
              label="Tipo de Recurrencia"
              labelId="recurrenceType-selection-label"
              id="recurrenceType"
              disabled={disabled}
            >
              <MenuItem value="">Seleccione un tipo</MenuItem>
              <MenuItem value="semanal">Semanal</MenuItem>
              <MenuItem value="quincenal">Quincenal</MenuItem>
              <MenuItem value="mensual">Mensual</MenuItem>
              <MenuItem value="anual">Anual</MenuItem>
              {/* ⚠️ NO incluir "diaria" - ese es específico de GroupCampaigns con horarios múltiples */}
            </Field>
            {touched.recurrenceType && errors.recurrenceType && (
              <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '3px', display: 'block' }}>
                {errors.recurrenceType}
              </Typography>
            )}
          </FormControl>
        </Grid>
        
        {/* Intervalo */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Intervalo"
            name="recurrenceInterval"
            type="number"
            value={values.recurrenceInterval === undefined || values.recurrenceInterval === null || values.recurrenceInterval === "" ? "" : values.recurrenceInterval}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || val === null || val === undefined) {
                setFieldValue('recurrenceInterval', "");
              } else {
                const numVal = parseInt(val, 10);
                setFieldValue('recurrenceInterval', isNaN(numVal) ? "" : numVal);
              }
            }}
            disabled={disabled}
            helperText={
              touched.recurrenceInterval && errors.recurrenceInterval 
                ? errors.recurrenceInterval 
                : "Ejemplo: 1 = cada semana, 2 = cada 2 semanas/meses"
            }
            inputProps={{ min: 1, step: 1 }}
            error={touched.recurrenceInterval && Boolean(errors.recurrenceInterval)}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField || ''}
          />
        </Grid>
        
        {/* Días de la Semana (solo si tipo = "semanal") */}
        {values.recurrenceType === "semanal" && (
          <Grid xs={12} item>
            <FormControl 
              variant="outlined" 
              fullWidth 
              margin="dense"
              className={`${classes.formControl || ''} ${classes.selectField || ''}`}
            >
              <InputLabel id="recurrenceDaysOfWeek-selection-label">
                Días de la Semana
              </InputLabel>
              <Select
                multiple
                value={selectedDays}
                onChange={(e) => {
                  const days = e.target.value;
                  setFieldValue('recurrenceDaysOfWeek', JSON.stringify(days));
                }}
                disabled={disabled}
                labelId="recurrenceDaysOfWeek-selection-label"
                label="Días de la Semana"
                error={touched.recurrenceDaysOfWeek && Boolean(errors.recurrenceDaysOfWeek)}
                renderValue={(selected) => {
                  return selected.map((day) => dayNames[day]).join(', ');
                }}
              >
                {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                  <MenuItem key={day} value={day}>
                    <Checkbox checked={selectedDays.includes(day)} />
                    <ListItemText primary={dayNames[day]} />
                  </MenuItem>
                ))}
              </Select>
              {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && (
                <Typography variant="caption" color="error" style={{ marginLeft: '14px', marginTop: '3px' }}>
                  {errors.recurrenceDaysOfWeek}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}
        
        {/* Día del Mes (solo si tipo = "mensual") */}
        {values.recurrenceType === "mensual" && (
          <Grid xs={12} md={6} item>
            <TextField
              label="Día del Mes"
              name="recurrenceDayOfMonth"
              type="number"
              value={values.recurrenceDayOfMonth || ''}
              onChange={(e) => setFieldValue('recurrenceDayOfMonth', parseInt(e.target.value) || null)}
              disabled={disabled}
              helperText="Día del mes (1-31)"
              inputProps={{ min: 1, max: 31 }}
              error={touched.recurrenceDayOfMonth && Boolean(errors.recurrenceDayOfMonth)}
              variant="outlined"
              margin="dense"
              fullWidth
              className={classes.textField || ''}
            />
          </Grid>
        )}
        
        {/* Fecha Final (Opcional) */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Fecha Final (Opcional)"
            name="recurrenceEndDate"
            type="datetime-local"
            value={values.recurrenceEndDate || ''}
            onChange={(e) => setFieldValue('recurrenceEndDate', e.target.value)}
            disabled={disabled}
            helperText="Dejar vacío para recurrencia infinita"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField || ''}
          />
        </Grid>
        
        {/* Máximo de Ejecuciones (Opcional) */}
        <Grid xs={12} md={6} item>
          <TextField
            label="Máximo de Ejecuciones (Opcional)"
            name="maxExecutions"
            type="number"
            value={values.maxExecutions || ''}
            onChange={(e) => setFieldValue('maxExecutions', parseInt(e.target.value) || null)}
            disabled={disabled}
            helperText="Dejar vacío para ilimitado"
            inputProps={{ min: 1 }}
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.textField || ''}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RecurrenceTab;
